import { usdFormat } from "../../../../utils";
import { ProductMixSummaryReportRow } from "./type";
import {ColDef} from "ag-grid-community";

const generateColDefs = (): ColDef<ProductMixSummaryReportRow>[] => [
  {
    headerName: 'Category Name',
    valueGetter: p => p.data?.product_type,
    pinned: 'left',
    filter: 'agTextColumnFilter',
    cellStyle: params => {
      if (params.value === 'Total') {
        return { fontWeight: 'bold' };
      }
      return null;
    }
  },
  {
    headerName: 'Total - # of units sold',
    valueGetter: p => p.data?.total_units_sold,
    valueFormatter: params => params.value ?? 0,
    cellStyle: params => {
      if (params.data?.product_type === 'Total') {
        return { fontWeight: 'bold' };
      }
      return null;
    }
  },
  {
    headerName: 'Total Gross Sales',
    valueGetter: p => p.data?.total_gross_sales,
    valueFormatter: params => usdFormat.format(params.value ?? 0),
    cellStyle: params => {
      if (params.data?.product_type === 'Total') {
        return { fontWeight: 'bold' };
      }
      return null;
    }
  },
  {
    headerName: 'Total Tax',
    valueGetter: p => p.data?.total_tax,
    valueFormatter: params => usdFormat.format(params.value ?? 0),
    cellStyle: params => {
      if (params.data?.product_type === 'Total') {
        return { fontWeight: 'bold' };
      }
      return null;
    }
  },
  {
    headerName: 'Total Discount',
    valueGetter: params => {
      return params.data?.total_discounts;
    },
    valueFormatter: params => usdFormat.format(params.value ?? 0),
    cellStyle: params => {
      if (params.data?.product_type === 'Total') {
        return { fontWeight: 'bold' };
      }
      return null;
    }
  },
  {
    headerName: 'Total Promo Codes',
    valueGetter: params => {
      return params.data?.total_promo_discounts;
    },
    valueFormatter: params => usdFormat.format(params.value ?? 0),
    cellStyle: params => {
      if (params.data?.product_type === 'Total') {
        return { fontWeight: 'bold' };
      }
      return null;
    }
  },
  {
    headerName: 'Total Net Sales',
    valueGetter: params => {
      return params.data?.total_net_sales;
    },
    valueFormatter: params => usdFormat.format(params.value ?? 0),
    cellStyle: params => {
      if (params.data?.product_type === 'Total') {
        return { fontWeight: 'bold' };
      }
      return null;
    }
  },
  {
    headerName: '# of Units Sold - Cash',
    valueGetter: params => {
      return params.data?.cash_units_sold;
    },
    cellStyle: params => {
      if (params.data?.product_type === 'Total') {
        return { fontWeight: 'bold' };
      }
      return null;
    }
  },
  {
    headerName: '# of Units Sold - Cash Balance',
    valueGetter: params => {
      return params.data?.cash_balance_units_sold;
    },
    cellStyle: params => {
      if (params.data?.product_type === 'Total') {
        return { fontWeight: 'bold' };
      }
      return null;
    }
  },
  {
    headerName: '# of Units Sold - Credit',
    valueGetter: params => {
      return params.data?.credit_units_sold;
    },
    cellStyle: params => {
      if (params.data?.product_type === 'Total') {
        return { fontWeight: 'bold' };
      }
      return null;
    }
  },
  {
    headerName: '# of Units Sold - RFID',
    valueGetter: params => {
      return params.data?.rfid_units_sold;
    },
    cellStyle: params => {
      if (params.data?.product_type === 'Total') {
        return { fontWeight: 'bold' };
      }
      return null;
    }
  },
  {
    headerName: '# of Units Sold -Tokens',
    valueGetter: params => {
      return params.data?.token_units_sold;
    },
    valueFormatter: params => params.value ?? 0,
    cellStyle: params => {
      if (params.data?.product_type === 'Total') {
        return { fontWeight: 'bold' };
      }
      return null;
    }
  },
];

export const colDefs: ColDef<ProductMixSummaryReportRow>[] = generateColDefs();

export const formatData = (data: ProductMixSummaryReportRow[]) => {
  if (!data) return [];

  let totalRow: ProductMixSummaryReportRow = {
    id: "",
    product_type: "Total",
    total_gross_sales: 0,
    total_tax: 0,
    total_discounts: 0,
    total_refunds: 0,
    total_net_sales: 0,
    cash_units_sold: 0,
    credit_units_sold: 0,
    rfid_units_sold: 0,
    cash_balance_units_sold: 0,
    token_units_sold: 0,
    total_units_sold: 0,
    location_id: 0,
    transaction_date: "",
    gross_cash_sales: 0,
    gross_cash_balance_sales: 0,
    gross_credit_sales: 0,
    gross_rfid_sales: 0,
    gross_promo_balance_sales: 0,
    gross_token_sales: 0,
    total_promo_discounts: 0,
    username: "",
  };
  const groupedRows: Record<string, ProductMixSummaryReportRow> = {};

  data.forEach((curr) => {
    const product_type = curr.product_type;
    const total_gross_sales = (curr.gross_cash_balance_sales + curr.gross_cash_sales + curr.gross_credit_sales + curr.gross_promo_balance_sales + curr.gross_rfid_sales + curr.gross_token_sales) / 100;
    const total_discounts = curr.total_discounts / 100;
    const total_promo_discounts = curr.total_promo_discounts / 100;
    const total_refunds = curr.total_refunds / 100;
    const total_net_sales = ((curr.gross_cash_balance_sales + curr.gross_cash_sales + curr.gross_credit_sales + curr.gross_promo_balance_sales + curr.gross_rfid_sales + curr.gross_token_sales) / 100) -
        ((curr.gross_token_sales + curr.total_refunds + curr.gross_promo_balance_sales + curr.total_discounts + curr.total_promo_discounts + curr.total_tax) / 100);
    const cash_units_sold = curr.cash_units_sold;
    const cash_balance_units_sold = curr.cash_balance_units_sold;
    const credit_units_sold = curr.credit_units_sold;
    const rfid_units_sold = curr.rfid_units_sold;
    const total_units_sold = curr.total_units_sold;
    const total_tax = curr.total_tax / 100;
    const token_units_sold = curr.token_units_sold;

    if (groupedRows[product_type]) {
      const row: ProductMixSummaryReportRow = groupedRows[product_type];
      row.total_gross_sales += total_gross_sales;
      row.total_tax += total_tax;
      row.total_discounts += total_discounts;
      row.total_promo_discounts += total_promo_discounts;
      row.total_refunds += total_refunds;
      row.total_net_sales += total_net_sales;
      row.cash_units_sold += cash_units_sold;
      row.cash_balance_units_sold += cash_balance_units_sold;
      row.credit_units_sold += credit_units_sold;
      row.rfid_units_sold += rfid_units_sold;
      row.token_units_sold += token_units_sold;
      row.total_units_sold += total_units_sold;
    } else {
      groupedRows[product_type] = {
        ...curr,
        id: product_type,
        total_units_sold,
        total_gross_sales,
        total_tax,
        product_type,
        total_discounts,
        total_promo_discounts,
        total_net_sales,
        token_units_sold,
        cash_units_sold,
        cash_balance_units_sold,
        credit_units_sold,
        rfid_units_sold
      };
    }
    totalRow.total_gross_sales += total_gross_sales;
    totalRow.total_tax += total_tax;
    totalRow.total_discounts += total_discounts;
    totalRow.total_promo_discounts += total_promo_discounts;
    totalRow.total_refunds += total_refunds;
    totalRow.total_net_sales += total_net_sales;
    totalRow.cash_units_sold += cash_units_sold;
    totalRow.cash_balance_units_sold += cash_balance_units_sold;
    totalRow.credit_units_sold += credit_units_sold;
    totalRow.rfid_units_sold += rfid_units_sold;
    totalRow.token_units_sold += token_units_sold;
    totalRow.total_units_sold += total_units_sold;
  });
  groupedRows[totalRow.id] = totalRow;

  return Object.values(groupedRows);
};